// src/components/profile/DeleteAccountButton.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api, showNotification, NOTIFICATION_TYPES } from '../../utils/apiClient';
import { STORAGE_KEYS } from '../../utils/constants';
import {
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';

const DeleteAccountButton = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDeleteAccount = async () => {
    if (confirmText.toUpperCase() !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    try {
      setIsDeleting(true);
      setError('');

      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token) {
        throw new Error('Authentication required');
      }

      const result = await api.delete('/user/account/delete', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      showNotification('Account deleted successfully', NOTIFICATION_TYPES.SUCCESS);
      
      // Clear all storage
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      // Logout and redirect
      if (logout) logout();
      
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);

    } catch (err) {
      console.error('Delete account error:', err);
      
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete account';
      const errorType = err.response?.data?.error;
      
      if (errorType === 'ACTIVE_BOOKING_EXISTS') {
        setError(errorMessage);
        showNotification(errorMessage, NOTIFICATION_TYPES.WARNING);
      } else {
        setError(errorMessage);
        showNotification(errorMessage, NOTIFICATION_TYPES.ERROR);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Delete Account Button */}
      <button 
        onClick={() => setShowConfirmModal(true)}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium text-sm transition-all border border-red-200 hover:border-red-300"
      >
        <TrashIcon className="h-5 w-5" />
        Delete Account
      </button>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-slide-up">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <ShieldExclamationIcon className="h-8 w-8 text-red-600" />
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Account</h3>
                <p className="text-sm text-gray-600">
                  This action cannot be undone. All your data will be permanently deleted.
                </p>
              </div>

              {/* Warning List */}
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-semibold text-red-900 mb-3">⚠️ Warning: You will lose</p>
                <ul className="space-y-2 text-sm text-red-800">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-0.5">•</span>
                    <span>All your personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-0.5">•</span>
                    <span>Booking history and preferences</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-0.5">•</span>
                    <span>Saved payment methods</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 mt-0.5">•</span>
                    <span>Account recovery will not be possible</span>
                  </li>
                </ul>
              </div>

              {/* Confirmation Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type <span className="font-bold text-red-600">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => {
                    setConfirmText(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="Type DELETE"
                  disabled={isDeleting}
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {error && (
                  <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    {error}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmText('');
                    setError('');
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || confirmText.toUpperCase() !== 'DELETE'}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default DeleteAccountButton;
